import {
  APP_TO_BUILD,
  ElectricalPracticeTestType,
  ElectricalUSPracticeTestType,
  IbewAptitudePracticeTestType as IbewPracticeTestType,
  PlumbingPracticeTestType,
} from "../../appsConfig";
import { getCurrentLanguage } from "./index";

type PracticeCopyField =
  | 'title'
  | 'description'
  | 'mainDescription'
  | 'breakdownDescription'
  | 'resultsTitle';

type PracticeCopyEntry = Partial<Record<PracticeCopyField, string>>;
type PracticeCopyMap = Record<string, Record<string, PracticeCopyEntry>>;

const PRACTICE_TYPE_COPY_ES: PracticeCopyMap = {
  electrical: {
    [ElectricalPracticeTestType.QUICK]: {
      title: 'Práctica rápida',
      description: 'Practica 5 preguntas aleatorias del examen',
      mainDescription:
        'Evaluación rápida de tu capacidad general para el examen. El modo de práctica incluye preguntas aleatorias de todos los temas:\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Sistemas de cableado\n- Sistemas de motores y control\n- Sistemas de comunicación\n- Sistemas especiales e industriales\n- Conceptos de circuitos',
    },
    [ElectricalPracticeTestType.UPDATES_2024]: {
      title: 'Actualizaciones CEC 2024',
      description:
        'Practica preguntas del examen nuevas o actualizadas en las revisiones de 2024',
      mainDescription:
        'Practica 25 preguntas de examen nuevas o modificadas por las actualizaciones 2024 del Código Eléctrico Canadiense.',
    },
    [ElectricalPracticeTestType.STANDARD]: {
      title: 'Práctica estándar',
      description:
        'Mini examen con cobertura completa de temas y ponderación real',
      mainDescription:
        'Evalúa tu capacidad general en todas las materias del examen, según la ponderación real.\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Sistemas de cableado\n- Sistemas de motores y control\n- Sistemas de comunicación\n- Sistemas especiales e industriales\n- Conceptos de circuitos',
    },
    [ElectricalPracticeTestType.OCCUPATIONAL_SKILLS]: {
      title: 'Habilidades ocupacionales',
      resultsTitle: 'Ocupacional',
      description:
        'Conocimiento y teoría del oficio, definiciones, distancias, equipos de prueba y seguridad',
      mainDescription:
        'Evalúa tu capacidad para realizar habilidades ocupacionales comunes. Incluye temas de seguridad, herramientas y equipos, organización, componentes de soporte y puesta en marcha y retirada de sistemas eléctricos.',
      breakdownDescription:
        'Conocimiento y teoría del oficio, definiciones, distancias, equipos de prueba y seguridad',
    },
    [ElectricalPracticeTestType.SERVICE_DISTRIBUTION]: {
      title: 'Servicio y distribución',
      resultsTitle: 'Servicio y distribución',
      description:
        'Dimensionamiento de servicios, transformadores, puesta a tierra y unión, alta tensión y protección',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de generación, distribución y servicio. Incluye temas sobre servicios del consumidor y de suministro, medidores, dispositivos de protección, equipos de distribución, UPS, sistemas de unión y puesta a tierra, generación, energías renovables, alta tensión y transformadores.',
      breakdownDescription:
        'Dimensionamiento de servicios, transformadores, puesta a tierra y unión, alta tensión y protección',
    },
    [ElectricalPracticeTestType.WIRING_SYSTEMS]: {
      title: 'Sistemas de cableado',
      resultsTitle: 'Métodos de cableado',
      description:
        'Circuitos derivados, dispositivos, iluminación, dimensionamiento de conductores y tuberías, y cálculo de cajas',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de cableado. Incluye temas sobre canalizaciones, cables, envolventes, circuitos derivados, HVAC, calefacción eléctrica, iluminación de emergencia y sistemas de protección catódica.',
      breakdownDescription:
        'Circuitos derivados, dispositivos, iluminación, dimensionamiento de conductores y tuberías, y cálculo de cajas',
    },
    [ElectricalPracticeTestType.MOTOR_CONTROL_SYSTEMS]: {
      title: 'Sistemas de motores y control',
      resultsTitle: 'Motores y controles',
      description:
        'Dimensionamiento de alimentadores, conductores y protección de motores, controles y HVAC/compresores',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener motores y sistemas de control. Incluye arrancadores y controles de motores, variadores, motores y sistemas de control automatizados.',
      breakdownDescription:
        'Dimensionamiento de alimentadores, conductores y protección de motores, controles y HVAC/compresores de refrigeración',
    },
    [ElectricalPracticeTestType.COMMUNICATION_SYSTEMS]: {
      title: 'Sistemas de comunicación',
      resultsTitle: 'Comunicaciones',
      description:
        'Circuitos de comunicación, controles integrados y sistemas de señalización',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de señalización y comunicación. Incluye sistemas de alarma contra incendios, seguridad y vigilancia, voz/datos/video, antena comunitaria, llamada de enfermería y automatización y control de edificios.',
      breakdownDescription:
        'Circuitos de comunicación, controles integrados y sistemas de señalización',
    },
    [ElectricalPracticeTestType.NO_CODEBOOK]: {
      title: 'Sin código',
      description:
        'Modo sin código que cubre teoría, conocimiento del oficio y mejores prácticas',
      mainDescription:
        'Contiene preguntas que no requieren consultar el código para responder.',
    },
    [ElectricalPracticeTestType.RED_SEAL_EXAM]: {
      title: 'Simulación de examen',
      resultsTitle: 'Simulación de examen',
      description: 'Practica un examen simulado con tiempo límite y sin pistas',
      mainDescription:
        'Simula el examen. Evalúa tu capacidad general en todas las materias según la ponderación real. Tiempo límite: 4 horas.\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Sistemas de cableado\n- Sistemas de motores y control\n- Sistemas de comunicación\n- Sistemas especiales e industriales\n- Conceptos de circuitos',
    },
    [ElectricalPracticeTestType.CALCULATION_ONLY]: {
      title: 'Cálculos',
      description:
        'Dimensionamiento de conductores u OC, carga, caída de voltaje y otros temas matemáticos basados en código',
      mainDescription:
        'Evalúa tu capacidad para aplicar lineamientos del CEC a preguntas de cálculo.\n\nTemas cubiertos:\n- Dimensionamiento de tuberías y conductores\n- Cálculos de carga\n- Caída de voltaje\n- Transformadores\n- Cálculos de sobrecorriente\n- ¡y más!',
    },
    [ElectricalPracticeTestType.CIRCUIT_CONCEPTS]: {
      title: 'Conceptos de circuitos',
      resultsTitle: 'Conceptos de circuitos',
      description:
        'Teoría eléctrica, ley de Ohm, circuitos en serie y paralelo, monofásicos y trifásicos',
      mainDescription:
        'Evalúa la comprensión fundamental de la teoría de circuitos eléctricos y sus aplicaciones prácticas.',
      breakdownDescription:
        'Teoría eléctrica y cálculos para circuitos de corriente continua, corriente alterna monofásica y trifásica',
    },
    [ElectricalPracticeTestType.SPECIAL_SYSTEMS]: {
      title: 'Sistemas especiales e industriales',
      resultsTitle: 'Sistemas especiales e industriales',
      description:
        'Entornos industriales, cuidado, áreas peligrosas y otras ubicaciones especiales',
      mainDescription:
        'Evalúa el conocimiento de sistemas eléctricos especializados y sus requisitos únicos de instalación y seguridad.',
      breakdownDescription:
        'Ocupaciones especiales, equipo industrial y condiciones como áreas peligrosas o de cuidado',
    },
    [ElectricalPracticeTestType.ONBOARDING]: {
      title: 'Práctica de bienvenida',
      resultsTitle: 'Examen de bienvenida',
      description: 'Practica 4 preguntas aleatorias para comenzar',
      mainDescription:
        'Una práctica rápida de bienvenida para familiarizarte con la app y el formato del examen.',
    },
  },
  electrical_us: {
    [ElectricalUSPracticeTestType.QUICK]: {
      title: 'Práctica rápida',
      description: 'Practica 6 preguntas aleatorias de NEC',
      mainDescription:
        'Evaluación rápida de tu capacidad general para el examen. El modo de práctica incluye preguntas aleatorias de todos los temas:\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Métodos y dispositivos de cableado\n- Motores y controles\n- Comunicaciones\n- Sistemas especiales',
    },
    [ElectricalUSPracticeTestType.STANDARD]: {
      title: 'Práctica estándar',
      description:
        'Mini examen con cobertura completa de temas y ponderación real',
      mainDescription:
        'Evalúa tu capacidad general en todas las materias del examen, según la ponderación real.\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Métodos y dispositivos de cableado\n- Motores y controles\n- Comunicaciones\n- Sistemas especiales',
    },
    [ElectricalUSPracticeTestType.OCCUPATIONAL_SKILLS]: {
      title: 'Habilidades ocupacionales',
      resultsTitle: 'Ocupacional',
      description:
        'Conocimiento y teoría del oficio, definiciones, distancias, equipos de prueba y seguridad',
      mainDescription:
        'Evalúa tu capacidad para realizar habilidades ocupacionales comunes.',
    },
    [ElectricalUSPracticeTestType.SERVICE_DISTRIBUTION]: {
      title: 'Servicio y distribución',
      resultsTitle: 'Servicio y distribución',
      description:
        'Dimensionamiento de servicios, transformadores, puesta a tierra y unión, alta tensión y protección',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de distribución y servicio.',
    },
    [ElectricalUSPracticeTestType.WIRING_SYSTEMS]: {
      title: 'Sistemas de cableado',
      resultsTitle: 'Métodos de cableado',
      description:
        'Circuitos derivados, dispositivos, iluminación, dimensionamiento de conductores y tuberías, y cálculo de cajas',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de cableado.',
    },
    [ElectricalUSPracticeTestType.MOTOR_CONTROL_SYSTEMS]: {
      title: 'Sistemas de motores y control',
      resultsTitle: 'Motores y controles',
      description:
        'Dimensionamiento de alimentadores, conductores y protección de motores, controles y HVAC/compresores',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener motores y sistemas de control.',
    },
    [ElectricalUSPracticeTestType.SPECIAL_CONDITIONS]: {
      title: 'Sistemas especiales',
      resultsTitle: 'Sistemas especiales',
      description:
        'Ocupaciones, equipos y condiciones especiales como áreas peligrosas o de cuidado básico',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener ocupaciones, equipos y condiciones especiales.',
    },
    [ElectricalUSPracticeTestType.COMMUNICATION_SYSTEMS]: {
      title: 'Sistemas de comunicación',
      resultsTitle: 'Comunicaciones',
      description:
        'Circuitos de comunicación, controles integrados y sistemas de señalización',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de señalización y comunicación.',
    },
    [ElectricalUSPracticeTestType.NO_CODEBOOK]: {
      title: 'Sin código',
      description:
        'Modo sin código que cubre teoría, conocimiento del oficio y mejores prácticas',
      mainDescription:
        'Contiene preguntas que no requieren consultar el código para responder.',
    },
    [ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION]: {
      title: 'Memorización del código',
      description:
        'Memoriza secciones del NEC y conceptos clave con práctica sin código',
      mainDescription:
        'Te ayuda a fijar las secciones y conceptos del NEC que aparecen una y otra vez. También practicarás preguntas de búsqueda para entrenarte a encontrar respuestas rápidamente en el código, incluso en áreas menos comunes como sistemas especiales y comunicaciones.\n\nNota: estas preguntas no son las del examen; sirven para desarrollar conocimiento del oficio y velocidad de búsqueda.\n\nTemas cubiertos:\n- Búsquedas en el código (incluyendo secciones 600-900)\n- Temperaturas de terminación\n- Motores\n- Conductores\n- Dimensionamiento de servicios\n- Caída de voltaje',
    },
    [ElectricalUSPracticeTestType.RED_SEAL_EXAM]: {
      title: 'Simulación de examen NEC',
      resultsTitle: 'Examen NEC',
      description: 'Practica un examen simulado con tiempo límite y sin pistas',
      mainDescription:
        'Simula el examen {exam_name}. Evalúa tu capacidad general en todas las materias según la ponderación real. Tiempo límite: {exam_time}.\n\nTemas cubiertos:\n- Habilidades ocupacionales\n- Servicio y distribución\n- Métodos y dispositivos de cableado\n- Motores y controles\n- Comunicaciones\n- Sistemas especiales',
    },
    [ElectricalUSPracticeTestType.CALCULATION_ONLY]: {
      title: 'Cálculos',
      description:
        'Dimensionamiento de conductores u OC, carga, caída de voltaje y otros temas matemáticos basados en código',
      mainDescription:
        'Evalúa tu capacidad para aplicar lineamientos del NEC a preguntas de cálculo.\n\nTemas:\n- Cálculos de carga\n- Dimensionamiento de tuberías y conductores\n- Caída de voltaje\n- Corriente de falla\n- Dimensionamiento de motores, conductores y sobrecorriente\n- Puesta a tierra de equipos\n- Fórmulas, tablas y reglas del NEC',
    },
    [ElectricalUSPracticeTestType.CHANGES_2023]: {
      title: 'Actualizaciones NEC 2023',
      description:
        'Practica preguntas sobre nuevas actualizaciones y revisiones del NEC 2023',
      mainDescription:
        'Evalúa tu comprensión de las últimas actualizaciones y revisiones del NEC 2023. Cubre nuevas regulaciones de seguridad, estándares de instalación actualizados, modificaciones a códigos existentes y tecnologías emergentes. Se enfoca en la aplicación práctica de estos cambios y su impacto en instalaciones y mantenimiento eléctricos.',
    },
    [ElectricalUSPracticeTestType.STATE_AMENDMENTS]: {
      title: 'Enmiendas estatales',
      description:
        'Practica preguntas sobre las enmiendas estatales al código NEC',
      mainDescription:
        'Evalúa tu conocimiento de las enmiendas estatales específicas al NEC. Cubre variaciones locales del código, requisitos de cumplimiento, instalaciones eléctricas, protocolos de seguridad y estándares de inspección. Se enfoca en la implementación e integración de las enmiendas estatales con las disposiciones estándar del NEC.',
    },
    [ElectricalUSPracticeTestType.ONBOARDING]: {
      title: 'Práctica de bienvenida',
      resultsTitle: 'Examen de bienvenida',
      description: 'Practica 4 preguntas aleatorias para comenzar',
      mainDescription:
        'Una práctica rápida de bienvenida para familiarizarte con la app y el formato del examen.',
    },
  },
  plumbing: {
    [PlumbingPracticeTestType.QUICK]: {
      title: 'Práctica rápida',
      description: 'Practica 7 preguntas aleatorias de Red Seal',
      mainDescription:
        'Evalúa el rendimiento general para el examen Red Seal. Incluye todos los temas: habilidades ocupacionales, ensamblaje de tuberías, sistemas de drenaje y alcantarillado, sistemas de distribución y sistemas hidrónicos y de vapor.',
    },
    [PlumbingPracticeTestType.STANDARD]: {
      title: 'Práctica estándar',
      description:
        'Mini examen con cobertura completa de temas y ponderación real',
      mainDescription:
        'Evalúa el rendimiento general para el examen Red Seal. Incluye todos los temas: habilidades ocupacionales, ensamblaje de tuberías, sistemas de drenaje y alcantarillado, sistemas de distribución y sistemas hidrónicos y de vapor.',
    },
    [PlumbingPracticeTestType.OCCUPATIONAL]: {
      title: 'Habilidades ocupacionales',
      resultsTitle: 'Habilidades ocupacionales',
      description:
        'Herramientas, seguridad, prácticas de levantamiento, manejo de materiales y procedimientos en obra',
      mainDescription:
        'Evalúa tu capacidad para realizar habilidades ocupacionales comunes. Incluye seguridad, herramientas y equipos, organización, componentes de soporte y actividades rutinarias del oficio para plomeros.',
    },
    [PlumbingPracticeTestType.PIPE]: {
      title: 'Ensamblaje de tuberías',
      resultsTitle: 'Ensamblaje de tuberías',
      description:
        'Materiales de tubería, técnicas de unión, separación, métodos de corte y trazado',
      mainDescription:
        'Evalúa tu capacidad para preparar y unir tuberías y tubos. Incluye inspección, corte, doblado y unión de distintos tipos de tubería.',
    },
    [PlumbingPracticeTestType.SEWAGE]: {
      title: 'Desagüe, drenaje y ventilación',
      resultsTitle: 'Desagüe, drenaje y ventilación',
      description:
        'Diseño de drenaje, dimensionamiento de ventilación, tipos de trampas, ubicación de registros y pendiente',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de alcantarillado o DWV (drenaje, desecho y ventilación). Incluye dimensionamiento, instalación, servicio y tratamiento de aguas residuales.',
    },
    [PlumbingPracticeTestType.SERVICE]: {
      title: 'Servicio y distribución',
      resultsTitle: 'Servicio y distribución',
      description:
        'Diseño del suministro de agua, prevención de contraflujo, control de presión, dimensionamiento y rutas',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener acometidas de agua, agua potable y sistemas de presión. Incluye dimensionamiento e instalación/servicio/pruebas de estos sistemas.',
    },
    [PlumbingPracticeTestType.FIXTURES]: {
      title: 'Accesorios y aparatos',
      resultsTitle: 'Accesorios y aparatos',
      description:
        'Inodoros, lavabos, lavavajillas, calentadores, unidades de tratamiento y reglas de instalación',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener accesorios y aparatos de plomería y equipos de tratamiento de agua. Incluye planificación e instalación de soportes.',
    },
    [PlumbingPracticeTestType.STEAM]: {
      title: 'Vapor y calefacción hidrónica',
      resultsTitle: 'Vapor y calefacción hidrónica',
      description:
        'Tuberías de vapor, circuitos hidrónicos, componentes de caldera, expansión y control de flujo',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de vapor de baja presión e hidrónicos. Incluye dimensionamiento, instalación, servicio y pruebas, además de unidades de transferencia y controles del sistema.',
    },
    [PlumbingPracticeTestType.SPECIALIZED]: {
      title: 'Plomería especializada',
      resultsTitle: 'Plomería especializada',
      description:
        'Gases médicos, solar, agua recuperada, tuberías especiales y control de riesgos',
      mainDescription:
        'Evalúa tu capacidad para instalar, dar servicio y mantener sistemas de tuberías especializadas y de proceso.',
    },
    [PlumbingPracticeTestType.NO_CODEBOOK]: {
      title: 'Sin código',
      description:
        'Modo sin código que cubre teoría, conocimiento del oficio y mejores prácticas',
      mainDescription:
        'Contiene preguntas que no requieren consultar el código para responder.',
    },
    [PlumbingPracticeTestType.RED_SEAL_EXAM]: {
      title: 'Simulación de examen Red Seal',
      resultsTitle: 'Examen Red Seal',
      description: 'Practica un examen simulado con tiempo límite y sin pistas',
      mainDescription:
        'Simula el examen Red Seal. Incluye todos los temas: habilidades ocupacionales, ensamblaje de tuberías, sistemas de drenaje y alcantarillado, sistemas de distribución, sistemas hidrónicos y de vapor, y sistemas especializados. Tiempo límite: 4 horas.',
    },
    [PlumbingPracticeTestType.ONBOARDING]: {
      title: 'Práctica de bienvenida',
      resultsTitle: 'Examen de bienvenida',
      description: 'Practica 4 preguntas aleatorias para comenzar',
      mainDescription:
        'Una práctica rápida de bienvenida para familiarizarte con la app y el formato del examen.',
    },
  },
  ibew_aptitude: {
    [IbewPracticeTestType.QUICK]: {
      title: 'Práctica rápida',
      description: 'Practica 5 preguntas aleatorias del examen IBEW',
      mainDescription:
        'Series cortas y enfocadas para reforzar conceptos clave y medir comprensión rápidamente. Ideales para repasar un solo tema o calentar antes de sesiones más largas. Dan retroalimentación rápida para mostrar fortalezas y vacíos.',
    },
    [IbewPracticeTestType.STANDARD]: {
      title: 'Práctica estándar',
      description:
        'Mini examen con cobertura completa de temas y ponderación real',
      mainDescription:
        'Series de práctica de longitud media que simulan el ritmo y la variedad del examen de aptitud IBEW. Cubren varios temas en una sola sesión para desarrollar precisión, ritmo y resistencia. Ayudan a identificar errores repetidos y mejorar la estrategia de examen.',
    },
    // Note: web enum is `APTITUDE_EXAM` (mobile uses `IBEW_EXAM`); same ordinal.
    [IbewPracticeTestType.APTITUDE_EXAM]: {
      title: 'Simulación de examen',
      description: 'Practica un examen simulado con tiempo límite y sin pistas',
      mainDescription:
        'Un examen de práctica completo, modelado de cerca según el formato y la dificultad del examen de aptitud IBEW. Diseñado para replicar condiciones reales, incluyendo tiempo y distribución de preguntas. Ideal para medir preparación real y ganar confianza.',
    },
    [IbewPracticeTestType.READING_COMPREHENSION]: {
      title: 'Comprensión lectora',
      resultsTitle: 'Comprensión lectora',
      description:
        'Interpreta pasajes y problemas verbales para identificar detalles clave y contexto',
      mainDescription:
        'Interpreta pasajes escritos y problemas verbales para identificar información relevante y comprender el contexto. Determina qué pregunta realmente se está haciendo antes de calcular. Filtra detalles innecesarios y concéntrate en restricciones, valores y relaciones clave.',
    },
    [IbewPracticeTestType.FRACTIONS_DECIMALS]: {
      title: 'Fracciones y decimales',
      resultsTitle: 'Fracción y decimal',
      description:
        'Realiza operaciones y conversiones entre fracciones y decimales',
      mainDescription:
        'Realiza operaciones aritméticas con fracciones y decimales, incluyendo suma, resta, multiplicación y división. Convierte entre fracciones, decimales y porcentajes según sea necesario. Aplica estas habilidades con precisión en cálculos aislados y problemas reales.',
    },
    [IbewPracticeTestType.GRAPHS_FUNCTIONS]: {
      title: 'Gráficas y funciones',
      resultsTitle: 'Gráficas y funciones',
      description: 'Lee, grafica e interpreta gráficas lineales y cuadráticas',
      mainDescription:
        'Lee e interpreta gráficas para entender relaciones entre variables. Grafica funciones lineales y cuadráticas usando tablas, intersecciones o ecuaciones dadas. Analiza características clave como pendiente, dirección, intersecciones y puntos de cruce.',
    },
    [IbewPracticeTestType.ORDER_OF_OPERATIONS]: {
      title: 'Orden de operaciones',
      resultsTitle: 'Orden de operaciones',
      description:
        'Aplica BEDMAS/PEMDAS para resolver expresiones correctamente',
      mainDescription:
        'Aplica correctamente el orden de operaciones usando BEDMAS o PEMDAS. Evalúa expresiones paso a paso para evitar errores comunes. Reconoce cómo paréntesis, exponentes y la multiplicación o división afectan el resultado.',
    },
    [IbewPracticeTestType.POLYNOMIAL_OPERATIONS]: {
      title: 'Operaciones con polinomios y factorización',
      resultsTitle: 'Operaciones con polinomios y factorización',
      description: 'Simplifica, expande y factoriza expresiones polinómicas',
      mainDescription:
        'Simplifica y manipula expresiones polinómicas mediante suma, resta, multiplicación y división. Expande expresiones usando la propiedad distributiva y combina términos semejantes. Factoriza polinomios para encontrar raíces o simplificar la resolución.',
    },
    [IbewPracticeTestType.QUADRATICS]: {
      title: 'Cuadráticas',
      resultsTitle: 'Cuadráticas',
      description:
        'Resuelve ecuaciones cuadráticas por factorización o fórmula',
      mainDescription:
        'Resuelve ecuaciones cuadráticas usando factorización, completar el cuadrado o la fórmula cuadrática. Identifica el número y tipo de soluciones según la ecuación. Aplica estos métodos a problemas algebraicos y prácticos.',
    },
    [IbewPracticeTestType.SEQUENCES]: {
      title: 'Secuencias',
      resultsTitle: 'Secuencias',
      description:
        'Identifica patrones y encuentra términos faltantes en secuencias numéricas',
      mainDescription:
        'Reconoce patrones en secuencias numéricas y algebraicas. Determina si una secuencia es aritmética, geométrica o sigue otra regla. Usa el patrón para encontrar términos faltantes o predecir valores futuros.',
    },
    [IbewPracticeTestType.SLOPES_LINEAR]: {
      title: 'Pendientes y lineales',
      resultsTitle: 'Pendientes y lineales',
      description:
        'Calcula pendiente, intersecciones y escribe ecuaciones lineales',
      mainDescription:
        'Calcula la pendiente usando dos puntos o una gráfica. Identifica e interpreta las intersecciones con x e y. Escribe ecuaciones lineales en forma estándar, pendiente-intersección o punto-pendiente.',
    },
    [IbewPracticeTestType.SYSTEMS_OF_EQUATIONS]: {
      title: 'Sistemas de ecuaciones',
      resultsTitle: 'Sistemas de ecuaciones',
      description: 'Resuelve incógnitas usando sustitución o eliminación',
      mainDescription:
        'Resuelve sistemas de ecuaciones usando sustitución o eliminación. Determina si tienen una solución, ninguna o infinitas. Interpreta las soluciones en contextos reales donde aplican múltiples restricciones.',
    },
    [IbewPracticeTestType.WORD_LOGIC]: {
      title: 'Problemas verbales y de lógica',
      resultsTitle: 'Problemas verbales y de lógica',
      description: 'Traduce problemas reales a ecuaciones y razona soluciones',
      mainDescription:
        'Traduce problemas escritos a ecuaciones o expresiones matemáticas. Usa razonamiento lógico para elegir estrategias y pasos adecuados. Verifica que las soluciones tengan sentido dentro del contexto dado.',
    },
    [IbewPracticeTestType.ONBOARDING]: {
      title: 'Práctica de bienvenida',
      resultsTitle: 'Examen de bienvenida',
      description: 'Practica 4 preguntas aleatorias para comenzar',
      mainDescription:
        'Traduce problemas escritos a ecuaciones o expresiones matemáticas. Usa razonamiento lógico para elegir estrategias y pasos adecuados. Verifica que las soluciones tengan sentido dentro del contexto dado.',
    },
  },
};

function interpolate(
  text: string,
  params?: Record<string, string | number | null | undefined>,
): string {
  if (!params) {
    return text;
  }

  return Object.entries(params).reduce((result, [key, value]) => {
    return result.split(`{${key}}`).join(String(value ?? ''));
  }, text);
}

function getLocalizedEntry(
  testType: number | string,
): PracticeCopyEntry | null {
  const language = getCurrentLanguage();
  if (language !== 'es') {
    return null;
  }

  return PRACTICE_TYPE_COPY_ES[APP_TO_BUILD]?.[String(testType)] ?? null;
}

export function getLocalizedPracticeTypeField(
  testType: number | string,
  field: PracticeCopyField,
  fallback?: string | null,
  params?: Record<string, string | number | null | undefined>,
): string {
  const entry = getLocalizedEntry(testType);
  const value = entry?.[field] ?? fallback ?? '';
  return interpolate(value, params);
}

export function localizePracticeTypeData<
  T extends Partial<Record<PracticeCopyField, string>>,
>(
  testType: number | string,
  data: T | undefined,
  params?: Record<string, string | number | null | undefined>,
): T {
  // Tolerate undefined data — PRACTICE_TYPE_DATA[testType] is undefined for
  // enum values that don't have a registered entry (e.g. AI chat banners on
  // some app variants). Without this guard, callers crash on data.title.
  return {
    ...data,
    title: getLocalizedPracticeTypeField(testType, 'title', data?.title, params),
    description: getLocalizedPracticeTypeField(
      testType,
      'description',
      data?.description,
      params,
    ),
    mainDescription: getLocalizedPracticeTypeField(
      testType,
      'mainDescription',
      data?.mainDescription,
      params,
    ),
    breakdownDescription: getLocalizedPracticeTypeField(
      testType,
      'breakdownDescription',
      data?.breakdownDescription,
      params,
    ),
    resultsTitle: getLocalizedPracticeTypeField(
      testType,
      'resultsTitle',
      data?.resultsTitle,
      params,
    ),
  } as T;
}

export function getLocalizedPracticeTypeResultTitle(
  testType: number | string,
  fallback?: string | null,
): string {
  return getLocalizedPracticeTypeField(testType, 'resultsTitle', fallback);
}
